import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService as ToasterService } from 'ngx-toastr';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Pagination } from '@sd-portal/shared/models';
import { untilViewDestroyed } from '@sd-portal/shared/rxjs';

import { AuditFieldLite, AuditLogLite } from '../+models/audit-log-lite.model';
import { RolesAuditRole } from '../+models/roles-audit.model';
import { RolesAuditService } from '../roles-audit.service';

@Component({
  selector: 'sd-portal-role-management-audit',
  templateUrl: './role-management-audit.component.html',
  styleUrls: ['./role-management-audit.component.scss']
})
export class RoleManagementAuditComponent implements OnInit, OnDestroy {
  roles: RolesAuditRole[] = [];
  rolesLoading = false;
  rolesPagination$ = new BehaviorSubject<Pagination>(new Pagination());

  selectedRole: RolesAuditRole | null = null;
  logs: AuditLogLite[] = [];
  logsLoading = false;
  logsPagination$ = new BehaviorSubject<Pagination>(new Pagination());

  constructor(
    private readonly rolesAuditService: RolesAuditService,
    private readonly toasterService: ToasterService
  ) {}

  ngOnInit() {
    this.loadRoles(1, 10);
  }

  ngOnDestroy() {}

  loadRoles(pageNumber: number, pageSize: number) {
    this.rolesLoading = true;
    this.rolesAuditService
      .getRoles(pageNumber, pageSize)
      .pipe(
        untilViewDestroyed(this),
        catchError(() => {
          this.rolesLoading = false;
          this.toasterService.error('Failed to retrieve roles');
          return EMPTY;
        })
      )
      .subscribe(res => {
        this.rolesLoading = false;
        this.roles = res.resultSet || [];
        this.rolesPagination$.next({
          ...new Pagination(),
          totalCount: res.totalCount,
          pageSize: res.pageSize,
          pageNumber: res.pageNumber
        });
      });
  }

  onRolesPageChange(pagination: Pagination) {
    this.loadRoles(pagination.pageNumber, pagination.pageSize);
  }

  selectRole(role: RolesAuditRole) {
    this.selectedRole = role;
    this.logs = [];
    this.loadLogs(1, 10);
  }

  loadLogs(pageNumber: number, pageSize: number) {
    if (!this.selectedRole) {
      return;
    }
    this.logsLoading = true;
    this.rolesAuditService
      .getRoleLogs(this.selectedRole.id, pageNumber, pageSize)
      .pipe(
        untilViewDestroyed(this),
        catchError(() => {
          this.logsLoading = false;
          this.toasterService.error('Failed to retrieve audit logs for role');
          return EMPTY;
        })
      )
      .subscribe(res => {
        this.logsLoading = false;
        this.logs = res.resultSet || [];
        this.logsPagination$.next({
          ...new Pagination(),
          totalCount: res.totalCount,
          pageSize: res.pageSize,
          pageNumber: res.pageNumber
        });
      });
  }

  onLogsPageChange(pagination: Pagination) {
    this.loadLogs(pagination.pageNumber, pagination.pageSize);
  }

  roleTrackByFn(_index: number, role: RolesAuditRole) {
    return role.id;
  }

  logTrackByFn(_index: number, log: AuditLogLite) {
    return log.id;
  }

  private static readonly CLAIM_PREFIX_LABELS: Record<string, string> = {
    Pre: 'PreFlight',
    Post: 'PostFlight',
    Prof: 'Profile'
  };

  private static readonly CLAIM_ACCESS_LABELS: Record<string, string> = {
    RO: 'read',
    X: 'read & edit'
  };

  private static readonly CLAIM_VALUE_PATTERN = /^(Pre|Post|Prof)-(.+)\.(RO|X)$/;

  formatFieldValue(field: AuditFieldLite, value: string): string {
    if (field.name !== 'ClaimValue' || !value) {
      return value;
    }

    const match = value.match(RoleManagementAuditComponent.CLAIM_VALUE_PATTERN);
    if (!match) {
      return value;
    }

    const [, prefix, claimName, access] = match;
    const prefixLabel = RoleManagementAuditComponent.CLAIM_PREFIX_LABELS[prefix];
    const accessLabel = RoleManagementAuditComponent.CLAIM_ACCESS_LABELS[access];

    return `${prefixLabel}-${claimName} (${accessLabel})`;
  }
}
